<template>
  <section
    :class="(categories.length > 2) ? 'is--slider is--slider-categories' : 'is--slider-categories-top'"
  >
    <div class="container">
      <h2 class="section-head" v-if="heading">
        {{ heading }}
      </h2>

      <div
        class="categories-box-wrap"
        :class="[{ 'categories-box-grid' : isGrid }, (columns === 3) ? 'categories-box-grid__3' : 'categories-box-grid__4']"
      >
        <router-link
          class="categories-box"
          :to="categoryLink(category)"
          :key="category.id"
          v-for="(category, index) in loadedCategories"
        >
          <div class="categories-box-heading" v-if="categories.length <= 2">
            <div>{{ category.name }}</div>
          </div>

          <div class="categories-box-content">
            <h3>{{ category.name }}</h3>
            <span class="more">{{ category.zusatzzeile }}</span>
            <div class="transition-wrapper">
              <p>{{ category.category_linktext || category.name }}</p>
              <span class="btn btn-primary">
                <span :class="(categories.length > 2) ? 'hidden-xs' : ''">Artikel ansehen</span>
                <span class="visible-xs" v-if="(categories.length > 2)">Ansehen</span>
              </span>
            </div>
          </div>

          <div class="categories-box-image">
            <div class="box-badge-offer" v-if="category.sale_label">
              {{ category.sale_label }}
            </div>
            <img
              v-if="!isEagerCategory(category, index)"
              v-lazy="getThumbnail(category.image,600,600,'category')"
              :data-srcset="getThumbnail(category.image,600,600,'category') + ' 600w,' + getThumbnail(category.image,500,500,'category') + ' 500w,' + getThumbnail(category.image,400,400,'category') + ' 400w,' + getThumbnail(category.image,300,300,'category') + ' 300w'"
              :alt="category.name"
            >
            <img
              v-if="isEagerCategory(category, index)"
              :src="getThumbnail(category.image,600,600,'category')"
              :alt="category.name"
              loading="eager"
              v-bind="prioritizeLcp && index === 0 ? { fetchpriority: 'high' } : {}"
            >
          </div>
        </router-link>

        <div
          class="categories-extra-tile"
          v-if="categories.length % columns !== 0 && categories.length > 2 && showExtraTile"
        >
          <h5>Nichts passendes dabei? Wir beraten kostenlos & kompetent:</h5>
          <a :href="'tel:' + storeConfig('general/store_information/phone')" target="_blank">{{ phoneNumber }}</a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'

import { formatCategoryLink } from '@vue-storefront/core/modules/url/helpers'
import { ensureMenuCategoriesByIds } from 'theme/helpers/menu'

export default {
  data () {
    return {
      secondLevelCategories: this.$store.state['category-next'].menuCategories.filter(c => { return c.level === 2 })
        .sort((a, b) => a.position - b.position)
    }
  },
  props: {
    categories: {
      type: null,
      required: true
    },
    heading: {
      type: [String],
      required: false
    },
    className: {
      type: [String],
      required: false
    },
    isGrid: {
      type: [Boolean],
      required: false
    },
    columns: {
      type: [Number, String],
      required: false
    },
    showExtraTile: {
      type: [Boolean],
      required: false
    },
    sortByProp: {
      type: [Boolean],
      required: false,
      default: false
    },
    /** Category id (number or string) to eager-load first image; `false` disables. */
    preload: {
      type: [Number, String, Boolean],
      required: false,
      default: false
    },
    /**
     * Render the first N tile images eagerly (real <img src> instead of v-lazy)
     * so above-the-fold tiles show up in SSR HTML and don't wait for the
     * IntersectionObserver to fire on hydration. The remaining tiles stay
     * v-lazy and load on scroll. Default 0 preserves the legacy behaviour
     * for usages that only want the explicit `preload` opt-in.
     */
    eagerImageCount: {
      type: Number,
      required: false,
      default: 0
    },
    /** Set false when the parent page already batch-loaded these category ids in asyncData. */
    prefetchOnServer: {
      type: Boolean,
      default: true
    },
    /** First eager tile is the page LCP candidate (mobile homepage, category grids). */
    prioritizeLcp: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      storeConfig: 'storeConfig/getConfigValue'
    }),
    loadedCategories () {
      let categories = this.categories;
      if (typeof this.categories[0] === 'object') {
        categories = categories.map(a => a.id)
      }
      const filteredCategories = this.filterMenuCategories(categories);

      if (this.sortByProp) {
        return filteredCategories.sort((a, b) => {
          return this.categories.indexOf(a.id) - this.categories.indexOf(b.id)
        });
      }

      return filteredCategories
    },
    phoneNumber () {
      const phone = this.storeConfig('general/store_information/phone')
      return typeof phone === 'string' ? phone.replace(/^02/, '+49 (0) 2') : ''
    }
  },
  created () {
    this.ensureCategoriesLoaded(this.categories)
  },
  serverPrefetch () {
    if (!this.prefetchOnServer) {
      return Promise.resolve()
    }
    return this.ensureCategoriesLoaded(this.categories)
  },
  watch: {
    categories: async function (categories, oldVal) {
      await this.ensureCategoriesLoaded(categories)
    }
  },
  methods: {
    async ensureCategoriesLoaded (categories) {
      return this.loadCategories(categories)
    },
    async loadCategories (categories) {
      if (!categories?.length) {
        return
      }
      if (typeof categories[0] === 'object') {
        categories = categories.map(a => a.id)
      }
      await ensureMenuCategoriesByIds(this.$store, categories)
    },
    categoryLink (category) {
      return formatCategoryLink(category)
    },
    isPreloadCategory (category) {
      const p = this.preload
      if (p === false || p === null || p === undefined || p === '') {
        return false
      }
      return Number(p) === Number(category.id)
    },
    isEagerCategory (category, index) {
      return this.isPreloadCategory(category) || index < this.eagerImageCount
    },
    filterMenuCategories (categories) {
      return this.$store.state['category-next'].menuCategories.filter(category => {
        return categories.includes(category.id)
      })
    }
  },
  metaInfo () {
    const category = this.loadedCategories.find(c => this.isPreloadCategory(c)) ||
      (this.prioritizeLcp && this.loadedCategories[0])
    if (category && category.image) {
      return {
        link: [
          {
            rel: 'preload',
            href: this.getThumbnail(category.image, 600, 600, 'category'),
            as: 'image',
            fetchpriority: 'high'
          }
        ]
      }
    }
  }
}
</script>
